// React & core
import { useEffect, useMemo, useRef, useState } from "react";

// Animation / Routing
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router";

// Icons
import { X, BadgeCheck } from "lucide-react";

function classNames(...xs) {
  return xs.filter(Boolean).join(" ");
}

function Badge({ children, tone = "default", icon: Icon }) {
  const tones = {
    default: "bg-slate-100 text-slate-700",
    green: "bg-emerald-100 text-emerald-700",
    blue: "bg-blue-100 text-blue-700",
    amber: "bg-amber-100 text-amber-800",
    red: "bg-rose-100 text-rose-700",
    grey: "bg-neutral-100 text-neutral-700",
  };
  return (
    <span
      className={classNames(
        "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium",
        tones[tone]
      )}
    >
      {Icon ? <Icon size={14} /> : null}
      {children}
    </span>
  );
}

const SectionCard = ({ title, right, children }) => (
  <section className="rounded-[12px] bg-white p-[15px]">
    <header className="flex items-center justify-between pb-[20px]">
      <h3 className="text-[20px] font-medium tracking-tight text-[#0E121B]">
        {title}
      </h3>
      {right}
    </header>
    <div className="">{children}</div>
  </section>
);

export default function ProjectBrief({
  open = true,
  onClose = () => {},
  leadDetail,
}) {
  const containerRef = useRef(null);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[60] flex items-stretch justify-end bg-slate-900/40 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.aside
            ref={containerRef}
            initial={{ x: 40, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 40, opacity: 0 }}
            transition={{ type: "spring", stiffness: 380, damping: 32 }}
            className="relative mt-auto mr-[2vh] mb-auto ml-0 flex h-[96vh] w-[992px] max-w-[98vw] flex-col overflow-hidden rounded-3xl border border-solid border-slate-200 bg-gradient-to-b from-white to-slate-50 shadow-xl"
            aria-modal
            role="dialog"
          >
            {
              <>
                <div className="border-b border-solid border-slate-200 bg-white/90 p-4 backdrop-blur supports-[backdrop-filter]:bg-white/70 w-full">
                  <div className="flex flex-wrap items-center justify-between gap-3 w-full">
                    <div className="flex min-w-0 items-center gap-3">
                      <div className="flex items-center justify-center size-12 bg-gradient-to-br from-indigo-500/20 to-emerald-500/20 rounded-full overflow-hidden">
                        <p className="capitalize font-semi-bold text-base text-black">
                          {leadDetail?.customer_details?.name
                            ?.split(" ")
                            ?.map((n, i) => n.charAt(0))}
                        </p>
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <h2 className="truncate text-lg font-semibold tracking-tight text-slate-900">
                            {leadDetail?.customer_details?.name}
                          </h2>
                          <Badge
                            tone={
                              leadDetail?.status == "pending"
                                ? "blue"
                                : leadDetail?.status == "Approved"
                                ? "green"
                                : "red"
                            }
                            icon={BadgeCheck}
                          >
                            {leadDetail?.status}
                          </Badge>
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-slate-500">
                          <span className="inline-flex items-center gap-1">
                            {leadDetail?.invoiceid}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        className="inline-flex items-center gap-1.5 rounded-xl bg-[#0B54FF] px-3 py-2 text-sm font-medium text-white hover:bg-[#0849e0]"
                        onClick={onClose}
                        aria-label="Close"
                      >
                        <X className="size-4" /> Exit
                      </button>
                    </div>
                  </div>

                  {/* Quick actions */}
                  <div className="mt-4 flex flex-wrap items-center gap-2">
                    <div className="flex mt-[17px] gap-1 items-center">
                      <p className="text-[13px]">Project Teams:</p>
                      <span className="text-[13px] text-[rgba(14,18,27,0.6)]">
                        {" "}
                        {leadDetail?.teams?.map((each, i) => {
                          return (
                            <span
                              key={i}
                              className="text-[13px] text-[rgba(14,18,27,0.6)]"
                            >
                              {" "}
                              {each?.team_name}
                              {leadDetail?.teams.length - 1 == i ? "" : ","}
                            </span>
                          );
                        })}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="space-y-4 overflow-auto">
                  <SectionCard title="Project Brief">
                    {Array.isArray(leadDetail?.briefs) &&
                    leadDetail?.briefs?.length > 0 ? (
                      <div className=" flex flex-col gap-3">
                        {leadDetail?.briefs.map((item, index) => {
                          const formattedAnswer = item.answer
                            ? item.answer.replace(
                                /(https?:\/\/[^\s,]+)/g,
                                (url) => {
                                  try {
                                    const parts = url.split("/");
                                    const fileName = parts[parts.length - 1];

                                    // 🧩 Check if it looks like a file link
                                    const isFile =
                                      /\.[a-zA-Z0-9]{2,5}($|\?)/.test(fileName);

                                    let displayText;
                                    if (isFile) {
                                      // Example: Trina-Sandell---2025Resume.pdf
                                      displayText =
                                        decodeURIComponent(fileName);
                                    } else {
                                      // Example: linkedin.com/in/trina-sandell-2b040225
                                      const domain = new URL(
                                        url
                                      ).hostname.replace("www.", "");
                                      const path = new URL(
                                        url
                                      ).pathname.replace(/^\/|\/$/g, "");
                                      displayText = `${domain}/${path}`.slice(
                                        0,
                                        60
                                      ); // Trim long paths
                                    }

                                    return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-blue-600 underline break-all">${displayText}</a>`;
                                  } catch {
                                    return url;
                                  }
                                }
                              )
                            : "—";

                          // Add space between multiple links for readability
                          const spacedAnswer = formattedAnswer.replace(
                            /<\/a>\s*(?=<a )/g,
                            "</a> &nbsp;&nbsp;"
                          );

                          return (
                            <>
                              <div className="grid grid-cols-12 items-center gap-3 rounded-xl border border-solid border-slate-200 bg-white p-3 transition hover:bg-slate-50">
                                <div className="col-span-12 flex items-center gap-2 text-[13px] font-semibold text-slate-500">
                                  {item.question}
                                </div>
                                <div className="col-span-12 flex items-center justify-between gap-2 text-[13px] text-slate-800">
                                  <p
                                    className="text-sm text-slate-600 leading-relaxed whitespace-pre-line break-words turncate"
                                    dangerouslySetInnerHTML={{
                                      __html: spacedAnswer,
                                    }}
                                  ></p>
                                </div>
                              </div>
                            </>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-gray-400 italic text-sm flex justify-center items-center">
                        No brief data available.
                      </p>
                    )}
                  </SectionCard>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between gap-2 border-t border-solid border-slate-200 bg-white p-3 w-full"></div>
              </>
            }
          </motion.aside>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
